import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useQuery } from 'react-query';
import querystring from 'querystring';
import { omit } from 'lodash';
import { queryOptions } from '~/shared/constants';
import { useNavigate } from 'react-router-dom';
import {
  searchToObj,
  getSplitText,
  getSearchPath,
  getSearchBoxHeight,
  getSearchResults,
  cleanKey,
  sortByIndex,
  getCleanText,
} from '~/shared/utils';
import { fetchNavHirarchy } from '../../services/api';
import useDebounce from '../../hooks/useDebounce';
import SearchBox from './SearchBox';
import { KeyValue, ArrowString } from './renderers';
import { PageWrapper, Background, SearchResults, SearchBlock, NoSearchResults } from './styles';
import SearchResultsTable from './SearchResultsTable';
import SearchTabs, { SearchTabItem } from './SearchTabs';

export const searchBlocks: { [key: string]: any } = {
  keyValue: KeyValue,
  arrowString: ArrowString,
};

export default function Search() {
  const navigate = useNavigate();
  const location = useLocation();

  const queryStringParams = useMemo(() => searchToObj(location.search || ''), [location]);

  const { data }: any = useQuery(fetchNavHirarchy.key, fetchNavHirarchy, queryOptions);

  const routes = data?.data;

  const flattenedRoutes = useMemo(() => {
    if (!routes) return [];
    return routes.reduce((acc: any[], curr: any) => {
      if (!curr) return acc;
      if (curr.type === 'heatmap' && Array.isArray(curr.categories)) {
        return acc.concat(curr.categories);
      }
      acc.push(curr);
      return acc;
    }, []);
  }, [routes]);

  const { data: navHierarchy } = useQuery(fetchNavHirarchy.key, fetchNavHirarchy, queryOptions);
  const searchData = navHierarchy?.searchData || [];

  const isUserExpired =
    routes &&
    routes[0].categories[routes[0].categories.length - 1]?.items[0].categories[
      routes[0].categories[routes[0].categories.length - 1]?.items[0].categories.length - 1
    ].name === 'UserExpired';

  const [isSearchHidden, setIsSearchHidden] = useState<boolean>(true);
  const [searchResultsArr, setSearchResultsArr] = useState<{ [key: string]: any }[]>([{}]);
  const [searchResultsTotal, setSearchResultsTotal] = useState(0);
  const [searchResultsSection, setSearchResultsSection] = useState<string>('');
  const [searchTabs, setSearchTabs] = useState<any>([]);
  const [isOpen, setIsOpen] = useState<any>(false);

  const [value, setValue] = useState('1');

  const [keysToNames, setKeysToNames] = useState<any>({});
  const [keysToType, setKeysToType] = useState<any>({});

  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearchValue = useDebounce(searchValue, 200);
  const [cleanDebouncedSearchValue, setCleanDebouncedSearchValue] = useState('');

  useEffect(() => {
    setCleanDebouncedSearchValue(getCleanText(debouncedSearchValue));
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (!searchData || searchData.length === 0) return;
    getSearchResults(
      cleanDebouncedSearchValue,
      searchData,
      searchResultsSection,
      setValue,
      setSearchResultsSection,
      setKeysToType,
      setKeysToNames,
      setSearchTabs,
      setSearchResultsArr,
      setSearchResultsTotal,
      cleanKey,
      sortByIndex,
    );
  }, [cleanDebouncedSearchValue, searchResultsSection, searchData]);

  const handle = (event: any) => {
    setIsSearchHidden(false);
    return setSearchValue(event.target.value);
  };

  const handleReset = () => {
    setIsSearchHidden(true);
    setSearchValue('');
    setIsOpen(false);
  };

  const normalizePathWithIds = useCallback(
    (rawPath: string) => {
      if (!rawPath) return rawPath;
      const [pathname, query = ''] = rawPath.split('?');
      const segments = pathname.split('/').filter(Boolean);
      if (!segments.length || flattenedRoutes.length === 0) return rawPath;

      const decodeSegment = (segment: string) => {
        try {
          return decodeURIComponent(segment);
        } catch (error) {
          return segment;
        }
      };

      const getChildren = (node: any) => {
        if (Array.isArray(node?.categories) && node.categories.length > 0) return node.categories;
        if (Array.isArray(node?.items) && node.items.length > 0) return node.items;
        if (Array.isArray(node?.prisms) && node.prisms.length > 0) return node.prisms;
        return undefined;
      };

      const decodedCategory = decodeSegment(segments[0]);
      const categoryNode = flattenedRoutes.find((cat: any) => cat?.name === decodedCategory);
      if (!categoryNode) return rawPath;

      let children = getChildren(categoryNode);
      let index = 1;
      while (index < segments.length - 1 && children) {
        const decodedSegment = decodeSegment(segments[index]);
        const nextNode = children.find((child: any) => child?.name === decodedSegment);
        if (!nextNode) return rawPath;
        children = getChildren(nextNode);
        index += 1;
      }

      if (!children || children.length === 0) return rawPath;

      const lastSegmentOriginal = segments[segments.length - 1];
      const lastSegmentDecoded = decodeSegment(lastSegmentOriginal);
      const leafNode =
        children.find((child: any) => child?.name === lastSegmentDecoded) ||
        children.find((child: any) => child?.id === lastSegmentOriginal);

      if (!leafNode || !leafNode.id || leafNode.id === lastSegmentOriginal) return rawPath;

      const updatedSegments = [...segments];
      updatedSegments[updatedSegments.length - 1] = leafNode.id;

      const rebuiltPath = `/${updatedSegments.join('/')}`;
      return query ? `${rebuiltPath}?${query}` : rebuiltPath;
    },
    [flattenedRoutes],
  );

  const handleSearchResultClick = useCallback(
    (template: any) => {
      const path = getSearchPath(template, isUserExpired, setIsSearchHidden, setIsOpen);
      const normalizedPath = normalizePathWithIds(path);
      if (normalizedPath.split('?')[0] === location.pathname) {
        const [targetPathname, targetQueryString = ''] = normalizedPath.split('?');
        const mergedQueryParams = {
          ...omit(queryStringParams, ['Asset', 'Narrative']),
          ...omit(searchToObj(`?${targetQueryString}`), ['Formation Period']),
        };
        const mergedQueryString = querystring.stringify(mergedQueryParams);
        const nextPath = mergedQueryString.length > 0 ? `${targetPathname}?${mergedQueryString}` : targetPathname;
        navigate(nextPath as unknown as (string | Location) & Location);
      } else {
        navigate(normalizedPath as unknown as (string | Location) & Location);
      }
    },
    [isUserExpired, location, navigate, normalizePathWithIds, queryStringParams],
  );

  const handleClick = useCallback((e: any) => {
    if (e.target.id === 'closeSearch') {
      setSearchValue('');
      setIsSearchHidden(true);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, [handleClick]);

  const onBackgroundClick = useCallback(() => {
    setIsSearchHidden(true);
    setIsOpen(false);
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
    setSearchResultsSection(Number(newValue) === 0 ? '' : searchTabs[Number(newValue) - 1]);
  };

  useEffect(() => {
    const tab = searchResultsArr.filter((sr: any) => sr.name === searchResultsSection)?.[0]?.data?.length;
    if (tab === 0) setSearchResultsSection('');
  }, [searchResultsArr, searchResultsSection]);

  const searchBoxHeight = useMemo(() => {
    const headersNm = searchResultsArr.length - 1;
    const total =
      searchResultsSection === ''
        ? searchResultsTotal
        : searchResultsArr.filter((sr: any) => sr.name === searchResultsSection)[0].data?.length;
    const height = getSearchBoxHeight(total, searchResultsSection, headersNm - 1);
    return height;
  }, [searchResultsArr, searchResultsTotal, searchResultsSection]);

  const searchTabItems = useMemo<SearchTabItem[]>(
    () => [
      { value: '0', label: 'All' },
      ...searchTabs.map((tab: any, index: number) => ({
        value: String(index + 1),
        label: keysToNames[tab] ?? tab,
        hidden: (searchResultsArr.find((sr: any) => sr.name === tab)?.data?.length ?? 0) === 0,
      })),
    ],
    [keysToNames, searchResultsArr, searchTabs],
  );

  if (searchData?.length === 0) return <div />;

  const hasResults = searchResultsTotal > 0;

  return (
    <PageWrapper>
      <SearchBox
        value={searchValue}
        onFocus={() => setIsSearchHidden(false)}
        onValueChange={handle}
        onValueReset={handleReset}
        isSearchVisible={!isSearchHidden}
        setIsOpen={setIsOpen}
        placeholder={isOpen ? '' : 'Search MediaStats'}
      />
      {searchResultsArr?.length > 0 && !isSearchHidden && (
        <SearchResults id="sr" height={searchBoxHeight}>
          {hasResults && <SearchTabs value={value} items={searchTabItems} onChange={handleChange} />}
          <NoSearchResults $visible={!hasResults}>
            {`No results found for search term "${searchValue}"`}
          </NoSearchResults>
          {hasResults && (
            <SearchBlock>
              {searchResultsSection === ''
                ? searchResultsArr.map((arr: any, index: number) => {
                    if (!keysToType[arr.name]) return null;
                    const SearchBlockComponent = searchBlocks[keysToType[arr.name]];

                    return (
                      <SearchBlockComponent
                        key={`${arr.name}${index}`}
                        name={arr.name}
                        searchResults={searchResultsArr[index].data}
                        searchValue={cleanDebouncedSearchValue}
                        handleSearchResultClick={handleSearchResultClick}
                        getSplitText={(cellData: string) => getSplitText(cellData, cleanDebouncedSearchValue)}
                        keysToNames={keysToNames}
                      />
                    );
                  })
                : searchResultsArr
                    .filter((sr: any) => sr.name === searchResultsSection)
                    .map((arr: any, index: number) => {
                      if (!keysToType[arr.name]) return null;
                      return (
                        <SearchResultsTable
                          data={arr.data}
                          name={arr.name}
                          keysToType={keysToType}
                          key={`${arr.name}${index}`}
                          onRowClick={handleSearchResultClick}
                          searchValue={cleanDebouncedSearchValue}
                        />
                      );
                    })}
            </SearchBlock>
          )}
        </SearchResults>
      )}
      {!isSearchHidden && <Background id="closeSearch" onClick={onBackgroundClick} />}
    </PageWrapper>
  );
}
